import { Color, PlaneGeometry, ShaderMaterial, Mesh, Vector2, DoubleSide, MeshBasicMaterial, Vector3 } from 'three';
import vertexShader from '../shaders/grid/vertex.glsl';
import fragmentShader from '../shaders/grid/fragment.glsl';
import BackgroundScene from './BackgroundScene.js';
import Camera from './Camera.js';
import Sizes from '../utils/Sizes.js';

export default class Grid {
    constructor() {
        // Get the world singleton
        this.backgroundScene = BackgroundScene.getInstance();
        this.scene = this.backgroundScene.getScene();

        // Get sizes instance
        this.sizes = Sizes.getInstance();

        // Default grid size, will be recalculated based on camera
        this.gridSize = 60;
        this.gridDepth = 2; // Much less depth for the grid
        this.uniforms = {
            uTime: { value: 0.0 },
            uColor: { value: new Color(0x49d2f8) },
            uSecondaryColor: { value: new Vector3(0.08, 0.3, 0.5) }, // 004ab2 // 005472
            uBackgroundColor: { value: new Color(0x0c2879) }, // 162458
            uBackgroundColor2: { value: new Vector3(0.0, 0.15, 0.89) }, // 035cce
            uGridScaleY: { value: 5.0 }, // 4.2 // 8.5
            uGridScaleX: { value: 1.5 },
            uHorizontalOpacity: { value: 1.0 },
            uLineWidthX: { value: 0.03 },
            uLineWidthY: { value: 0.16 }, // 0.15
            uScrollSpeed: { value: 30.0 },
            uResolution: { value: new Vector2() },
            uGrainSize: { value: 0.01 },
            uGrainAmount: { value: 0.2 },
            uNoiseThreshold: { value: 0.5 },
            uNoiseContrast: { value: 1.0 },
            uVerticalGlowIntensity: { value: 0.6 },
        };

        this.init();
    }

    init() {
        // Get camera properties
        this.camera = Camera.getInstance().cameraBackground;
        if (!this.camera) {
            return;
        }

        // Set initial resolution
        this.uniforms.uResolution.value.set(
            this.sizes.width,
            this.sizes.height
        );

        // Calculate visible height at the target distance
        const fov = this.camera.fov * (Math.PI / 180); // Convert to radians
        const cameraPos = this.camera.position.clone();

        // Calculate where the bottom of camera's view frustum intersects the y=0 plane
        const halfFov = fov / 2;
        const intersectDistance = cameraPos.y / Math.tan(halfFov);

        // Calculate distance to the far edge of the grid
        const farEdgeDistance = intersectDistance + this.gridDepth;

        // Calculate the visible width at the far edge of the grid (horizon)
        // Using: visibleWidth = 2 * distance * tan(horizontalFov/2)
        const halfVerticalFov = fov / 2;
        const halfHorizontalFov = Math.atan(Math.tan(halfVerticalFov) * this.camera.aspect);

        // Calculate the visible width at the far edge distance
        const visibleWidthAtHorizon = 2 * farEdgeDistance * Math.tan(halfHorizontalFov);

        // Set grid width to match the visible width at the horizon
        this.gridSize = visibleWidthAtHorizon;

        // Create the grid floor with custom shader

        const gridGeometry = new PlaneGeometry(this.gridSize, this.gridDepth, 1, 1);
        const gridMaterial = new ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: this.uniforms,
            transparent: true,
            side: DoubleSide,
            extensions: {
                derivatives: "#extension GL_OES_standard_derivatives: enable"
            },
        });

        const testMaterial = new MeshBasicMaterial({
            color: 0x00e8f6
        });



        this.gridFloor = new Mesh(gridGeometry, gridMaterial);
        this.gridFloor.rotation.x = -Math.PI / 2;

        // Position grid at the intersection point
        this.gridFloor.position.set(
            0,
            0,
            cameraPos.z - intersectDistance - this.gridDepth/2
        );

        this.scene.add(this.gridFloor);
    }

    update(delta, movementSpeed) {
        // Update uniform time for shader animation
        this.uniforms.uTime.value += delta * movementSpeed;
    }

    resize() {
        if (this.camera && this.gridFloor) {
            const fov = this.camera.fov * (Math.PI / 180);
            const cameraPos = this.camera.position.clone();

            const halfFov = fov / 2;
            const intersectDistance = cameraPos.y / Math.tan(halfFov);

            // Calculate distance to the far edge of the grid
            const farEdgeDistance = intersectDistance + this.gridDepth;

            // Recalculate the visible width at the far edge based on new camera aspect ratio
            const halfVerticalFov = fov / 2;
            const halfHorizontalFov = Math.atan(Math.tan(halfVerticalFov) * this.camera.aspect);
            const visibleWidthAtHorizon = 2 * farEdgeDistance * Math.tan(halfHorizontalFov);

            // Update grid width and geometry
            this.gridSize = visibleWidthAtHorizon;

            // Replace grid geometry with new dimensions
            this.gridFloor.geometry.dispose();
            this.gridFloor.geometry = new PlaneGeometry(this.gridSize, this.gridDepth, 1, 1);

            // Maintain grid position aligned with bottom of view
            this.gridFloor.position.x = 0;
            this.gridFloor.position.y = 0;
            this.gridFloor.position.z = cameraPos.z - intersectDistance - this.gridDepth/2;

            // Update resolution uniform
            this.uniforms.uResolution.value.set(
                this.sizes.width,
                this.sizes.height
            );
        }
    }

    // Getter for other components to access
    getHorizonPosition() {
        // Return the far edge of the grid as the horizon position
        if (this.gridFloor) {
            // The grid is positioned at its center, so the far edge is at z - gridDepth/2
            return this.gridFloor.position.z - this.gridDepth/2;
        }
        // Fallback value if grid not initialized yet
        if (this.camera) {
            const fov = this.camera.fov * (Math.PI / 180);
            const intersectDistance = this.camera.position.y / Math.tan(fov/2);
            return this.camera.position.z - intersectDistance - this.gridDepth/2;
        }
        return -this.gridDepth/2;
    }

    getGridSize() {
        return {
            width: this.gridSize,
            depth: this.gridDepth
        };
    }
}
